import { ThemeProvider } from '@karehero/llama/ThemeProvider';
import { radius } from '@karehero/llama/theme/radius';
import {
  fontFamilyBody,
  fontFamilyHeader,
  fontWeightMedium,
  fontWeightRegular,
  fontWeightSemiBold,
} from '@karehero/llama/theme/mui/typography';

import { previewPalette, TextSecondary, TextTitle } from '@/palettePreview';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  type Theme,
  type ThemeOptions,
} from '@mui/material/styles';

// Register the prototype-only `xsmall` input size (used for dense desktop
// surfaces like the CAM filters) with MUI's size unions.
declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    xsmall: true;
  }
}
declare module '@mui/material/InputBase' {
  interface InputBasePropsSizeOverrides {
    xsmall: true;
  }
}
declare module '@mui/material/FormControl' {
  interface FormControlPropsSizeOverrides {
    xsmall: true;
  }
}
// Re-declared rather than inherited: llama registers these on its own copy of
// the MUI module (lib/llama/src/theme/mui/components.ts) and that file is not in
// this app's type graph, so `size='xsmall'` on a toggle only compiles here if
// this app says so too. Both, because the group passes its size to its children.
declare module '@mui/material/ToggleButtonGroup' {
  interface ToggleButtonGroupPropsSizeOverrides {
    xsmall: true;
  }
}
declare module '@mui/material/ToggleButton' {
  interface ToggleButtonPropsSizeOverrides {
    xsmall: true;
  }
}

// `display1`: the Lora italic, kept as its own variant now that the headings are
// Work Sans. Named for Figma's `Display 1`. A heading level says where something sits in the document; this says
// how it should feel, and the two were the same prop for as long as h1 was the
// only way to get the serif. Anything that wants the brand voice asks for it by
// name — a hero line, a page a screen is named after — and nothing gets it by
// accident from being the biggest heading on the screen.
// `mono`: caption metrics in a monospace face. Loft's own tooling writes paths,
// prop names and token values in mono — the inspect panel, the drill callouts,
// the foundations stories — and every one of those was a caption carrying
// `sx={{ fontFamily: 'monospace' }}`. This is deliberately NOT a brand variant
// and should never be ported to llama: it belongs to the environment, not to
// the product, and no member-facing screen uses it.
declare module '@mui/material/styles' {
  interface TypographyVariants {
    display1: React.CSSProperties;
    mono: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    display1?: React.CSSProperties;
    mono?: React.CSSProperties;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display1: true;
    mono: true;
  }
}

// One weight for the whole heading ramp: Work Sans Medium, which is what every
// `typography/h*` in Figma binds.
//
// This was two weights, semibold for h1–h2 and medium below, on the reasoning
// that the serif being replaced was semibold. Figma settles it at medium
// throughout, so the split goes. h1 and h2 get lighter.
//
// Colour is re-declared on every variant rather than inherited: llama bakes a
// literal colour into each one (`color: themeColor.palette.text.title` in its
// typography.ts, read from its own colour module at import time), so a heading
// carries llama's marine as a hex and MUI paints from that without ever
// consulting the palette. Setting `palette.text.title` alone changes nothing.
const heading = {
  fontFamily: fontFamilyBody,
  fontStyle: 'normal',
  fontWeight: fontWeightMedium,
  color: TextTitle,
} as const;

// Prototype-local theme tweaks layered on top of the llama design system.
// Kept here (not in lib/llama) so they only affect the designs app and never
// leak into production.
//
// Exported so inspect mode can diff these overrides against the llama base
// theme and DERIVE whether each tweak is still prototype-only (proposed) or has
// already shipped to prod (a now-redundant override). That keeps the
// proposed/shipped flag honest without anyone hand-maintaining a status — see
// components/inspect/themeStatus.ts.
export const prototypeThemeOverrides: ThemeOptions['components'] = {
  // NB: no MuiButton entry — the pill radius + disableElevation it used to set
  // shipped to the llama theme, so the override was redundant and was deleted
  // during an inspect-mode sweep. Buttons are pipe-through now. (MuiButtonGroup
  // below stays: llama doesn't set disableElevation on it.)
  MuiButtonGroup: {
    defaultProps: { disableElevation: true },
  },
  // PROPOSED. MUI does not paint table dividers in `palette.divider` — it
  // derives `palette.TableCell.border` as `lighten(divider, 0.88)`
  // (createThemeWithVars.js:275). On llama's divider (Neutral[300], #C6C3BC)
  // that lands on #F8F8F7, which is lighter than Gardenia (#F0EAE0), so every
  // table on the page background draws a line paler than the ground it sits on
  // and reads as having no dividers at all.
  //
  // MUI's derivation assumes a white page. Ours is not white, so the assumption
  // does not hold and the divider token is the right answer. llama should set
  // this too — it is not a prototype-only tweak, it affects any table in the
  // PWA that is not on white.
  //
  // A function `root` does not replace anything here: llama has no MuiTableCell
  // entry, so there is no object for the createTheme merge to lose.
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }: any) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      // Soft corners, matching the cards (llama radius.md).
      paper: { borderRadius: 12 },
    },
  },
  // Tighten the gap between the dialog title and its body (MUI defaults to
  // 16px bottom). 12px sits the subtitle a touch closer to the heading.
  MuiDialogTitle: {
    styleOverrides: {
      root: { paddingBottom: 12 },
    },
  },
  // PROPOSED. MUI 9 ships the accordion flush: its own base rules are
  // `AccordionSummary { padding: 0 }` and `AccordionDetails { padding: 8px }`,
  // where v5 had `0 16px` and `8px 16px 16px`. Paired with llama's elevation-0
  // Paper that leaves a surface with no border, no shadow and no inset — text
  // sitting hard against the page gutter with nothing saying it is a control.
  //
  // So it gets the card's surface, because that is what it is: llama's bordered
  // neutral.50 at radius.md, and the inset back.
  MuiAccordion: {
    styleOverrides: {
      root: ({ theme }: any) => ({
        backgroundColor: theme.palette.custom.neutral[50],
        border: `1px solid ${theme.palette.custom.neutral[200]}`,
        borderRadius: `${radius.md}px`,
        // MUI draws a hairline above every accordion but the first, to divide
        // items in a flush stack. These are separated cards, so it is a stray
        // line across the top of each one.
        '&::before': { display: 'none' },
        '&:not(:last-of-type)': { marginBottom: 8 },
        // MUI squares the inner corners of a stack so the items read as joined.
        // These are separate cards, so each keeps its own radius on all four.
        '&:first-of-type': {
          borderTopLeftRadius: `${radius.md}px`,
          borderTopRightRadius: `${radius.md}px`,
        },
        '&:last-of-type': {
          borderBottomLeftRadius: `${radius.md}px`,
          borderBottomRightRadius: `${radius.md}px`,
        },
        // The default collapses the radius on all but the outer corners, which
        // is right for a joined stack and wrong for separated cards.
        '&.Mui-expanded': { margin: 0, marginBottom: 8 },
        '&:last-of-type.Mui-expanded': { marginBottom: 0 },
      }),
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: { padding: '0 16px', minHeight: 56 },
      content: { margin: '12px 0' },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      // No top padding: the summary's own bottom margin is the gap, and adding
      // to it puts an answer further from its question than the question is
      // from the one above.
      root: { padding: '0 16px 16px' },
    },
  },
  MuiCard: {
    styleOverrides: {
      // A function `root` here REPLACES llama's object `root` in the
      // createTheme merge (deepmerge can't combine an object with a function),
      // so we must restore llama's base surface — otherwise cards lose their
      // bg/border and fall back to shape.borderRadius (radius.sm). Radius is
      // the token (radius.md), matching the llama default.
      root: ({ theme }: any) => ({
        backgroundColor: theme.palette.custom.neutral[50],
        border: `1px solid ${theme.palette.custom.neutral[200]}`,
        borderRadius: `${radius.md}px`,
        // Vertical flex so a card's action bar (CardActions) sinks to the
        // bottom edge: paired with MuiCardActionArea growing below, every
        // card in a stretched grid row lines its actions up along the base.
        // A no-op for single-child cards (they lay out the same as block).
        display: 'flex',
        flexDirection: 'column',
      }),
    },
  },
  // Coral on a filled button, opted into with className='coral'.
  //
  // llama's Button takes a variant, not a colour, and its primary is Marine
  // Green: the palette has coral as MUI's `secondary`, but the Button never
  // forwards a colour through. So this is the class, following the same opt-in
  // convention as the segmented control below — a plain button stays exactly as
  // production renders it, and nothing here hardcodes a hex.
  //
  // On the `contained` slot rather than `root`, and that is load-bearing. llama
  // writes its own root as a plain object holding textTransform and the pill
  // radius; a root written here as a function replaces that object outright rather
  // than merging with it, so every button in the app loses its radius and comes
  // back shouting in uppercase. `contained` is a slot llama leaves alone, so this
  // is additive — and it is the only slot the rule wants anyway, since recolouring
  // an outlined button means repainting its border and label too.
  MuiButton: {
    styleOverrides: {
      contained: ({ theme }: any) => ({
        '&.coral': {
          // Brand Coral (600), and a step darker pressed. It was 700, a shade
          // off the coral the profile's own button drew by hand.
          backgroundColor: theme.palette.custom.coral[600],
          color: theme.palette.custom.neutral[50],
          '&:hover': { backgroundColor: theme.palette.custom.coral[700] },
        },
      }),
    },
  },
  // The tappable card body grows to fill a flex-column card, so any
  // CardActions sit flush at the bottom (see MuiCard above). Inert outside a
  // flex card — flex-grow only applies to flex children.
  //
  // Its three interaction states are set here rather than at each call site,
  // because every clickable card in the app should answer a pointer the same way.
  // MUI's default is one translucent overlay used for both hover and keyboard
  // focus, which leaves the two indistinguishable and gives a keyboard user no
  // ring at all — so the overlay is off and each state says something different:
  // hover is a wash, pressed is a stronger one, and focus is a marine ring.
  MuiCardActionArea: {
    styleOverrides: {
      root: ({ theme }: any) => ({
        flexGrow: 1,
        // The card's own radius, so the focus ring rounds with the corner instead
        // of drawing a rectangle inside it. An outline follows the element's
        // border-radius, and the radius lives on the Card, not on this: without it
        // the ring is square on a soft-cornered card.
        borderRadius: `${radius.md}px`,
        // Guarded, so a touch device doesn't keep the wash after a tap.
        '@media (hover: hover)': {
          '&:hover': { backgroundColor: theme.palette.custom.neutral[100] },
        },
        '&:active': { backgroundColor: theme.palette.custom.neutral[200] },
        // Inset, so the ring follows the card's own radius rather than drawing a
        // rectangle around it. The Card clips it.
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      }),
      focusHighlight: { display: 'none' },
    },
  },
  MuiFilledInput: {
    variants: [
      {
        // size='xsmall': a notch below llama's small. The root height
        // is pinned so text inputs and selects come out identical
        // regardless of their internal markup.
        props: { size: 'xsmall' },
        style: {
          fontSize: 13,
          height: 30,
          '& .MuiInputBase-input': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 8,
            paddingRight: 8,
            fontSize: 13,
            height: '100%',
            boxSizing: 'border-box',
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            minHeight: 0,
          },
        },
      },
    ],
  },
  // The KareHero segmented control: a sunflower track with the selected option
  // as a coral pill. Opt in with className='segmented' — the same convention
  // llama uses for its own MuiTabs segmented control.
  //
  // Opt-in rather than default because production doesn't have this override at
  // all: over there a ToggleButtonGroup is stock MUI, so a plain group here has
  // to be stock MUI too or the prototypes stop being evidence about the real
  // app. The care search toolbar (care type, List/Map) is the case that proved
  // it — it has to match what ships.
  //
  // Warm-on-warm only works with separation + value contrast, so the group
  // insets each button (padding + gap) and the selected pill is deep coral on
  // light sunflower, with dark idle text — no flush orange-on-yellow edges.
  MuiToggleButtonGroup: {
    styleOverrides: {
      // Horizontal reads as pills on a pill track; stacked vertically that's too
      // round, so the vertical orientation drops to a gentle radius.
      root: ({ ownerState, theme }: any) => {
        const vertical = ownerState?.orientation === 'vertical';
        return {
          '&.segmented': {
            backgroundColor: theme.palette.custom.sunflower[400],
            border: 0,
            borderRadius: vertical ? 12 : 9999,
            padding: 4,
            gap: 4,
            // Scoped to the children from here rather than through the `grouped`
            // slot, because `grouped` is unconditional and would strip MUI's
            // between-button dividers and per-position radii from every plain
            // group on the page — the production look this override is trying
            // to leave alone.
            //
            // The track is the boundary in here, so the buttons give theirs up,
            // along with MUI's dividers and radii: each one is its own pill
            // floating on the track. Both selectors are listed so the selected
            // rule carries enough classes to beat MUI's own `&.Mui-selected`
            // regardless of stylesheet order.
            '& .MuiToggleButtonGroup-grouped, & .MuiToggleButtonGroup-grouped.Mui-selected':
              {
                margin: 0,
                border: 0,
                borderRadius: vertical ? 8 : 9999,
              },
            '& .MuiToggleButtonGroup-grouped': {
              fontWeight: 600,
              color: theme.palette.text.primary,
              '&:not(:first-of-type)': { marginLeft: 0, borderLeft: 0 },
              '&.Mui-disabled': { border: 0 },
              '&:hover': {
                backgroundColor: theme.palette.custom.sunflower[500],
              },
            },
            // The coral pill. It lived on MuiToggleButton until standalone
            // toggles went back to stock MUI; it belongs to the track anyway,
            // since a toggle with no track behind it has nothing to be a pill on.
            '& .MuiToggleButtonGroup-grouped.Mui-selected': {
              backgroundColor: theme.palette.custom.coral[700],
              color: theme.palette.custom.neutral[50],
              '&:hover': { backgroundColor: theme.palette.custom.coral[700] },
            },
          },
        };
      },
    },
  },
  // No MuiToggleButton override, and no group override off the `segmented` class.
  // Toggles are stock MUI here — llama's theme adds only an xsmall size variant —
  // so they render as production renders them, in KareHero's type and palette.
};

const PrototypeOverrides = ({ children }: { children?: React.ReactNode }) => (
  <MuiThemeProvider
    theme={(outer: Theme) => {
      const next = createTheme(outer, {
        components: prototypeThemeOverrides,
        // Preview of the simplified colour system from Figma. Layered here so
        // the prototypes render in the new palette without touching llama —
        // see palettePreview.ts for what changed and what is still unresolved.
        palette: previewPalette,
        // Headings, re-declared. This is not a second way of saying what the
        // palette already says: llama bakes a literal colour into every heading
        // variant (`color: themeColor.palette.text.title` in its typography.ts,
        // read from its own colour module at import time), so h1–h6 carry marine
        // as a hex and MUI paints from that without ever consulting the palette.
        // Setting `palette.text.title` alone changes nothing on screen.
        //
        // The family moves too. Every heading is Work Sans here, which llama and
        // Figma do not yet agree on: Figma has Lora italic on all six, llama has
        // it on h1–h3 and Work Sans Medium on h4–h6. A prototype override until
        // that is settled, so nothing is decided for production by a screen.
        //
        // Sizes, line heights and letter spacing stay llama's. h1's -1.5px was
        // tracked for a serif at 34px and is tight on a sans — worth a look once
        // the change has been seen on a few screens.
        //
        // Sizes, line heights and tracking are Figma's, read off
        // `typography/*` in the August file rather than inherited from llama.
        // llama's ramp was 34/24/20/18/16/14; Figma's is 34/24/24/20/20/16, so
        // h3 and h4 both grow and h5 lands on the same size as h4. That doubling
        // up is what the file says.
        typography: {
          h1: {
            ...heading,
            fontSize: '2.125rem',
            lineHeight: 1.167,
            letterSpacing: '-1.5px',
          },
          h2: {
            ...heading,
            fontSize: '1.5rem',
            lineHeight: 1.2,
            letterSpacing: '-0.5px',
          },
          h3: {
            ...heading,
            fontSize: '1.5rem',
            lineHeight: 1.167,
            letterSpacing: 0,
          },
          h4: {
            ...heading,
            fontSize: '1.25rem',
            lineHeight: 1.235,
            letterSpacing: '0.25px',
          },
          h5: {
            ...heading,
            fontSize: '1.25rem',
            lineHeight: 1.334,
            letterSpacing: 0,
          },
          h6: {
            ...heading,
            fontSize: '1rem',
            lineHeight: 1.6,
            letterSpacing: '0.15px',
          },
          // Re-declared for the same reason as the headings: llama bakes
          // text.secondary into these at import time, and their metrics move.
          subtitle1: {
            fontFamily: fontFamilyBody,
            fontWeight: fontWeightRegular,
            fontSize: '1rem',
            lineHeight: 1.75,
            letterSpacing: '0.15px',
            color: TextSecondary,
          },
          subtitle2: {
            fontFamily: fontFamilyBody,
            fontWeight: fontWeightMedium,
            fontSize: '0.875rem',
            lineHeight: 1.57,
            letterSpacing: '0.1px',
            color: TextSecondary,
          },
          // Both were 12px. Figma puts them at 14, which is the change most
          // likely to be felt: captions and overlines are everywhere.
          caption: {
            fontFamily: fontFamilyBody,
            fontWeight: fontWeightRegular,
            fontSize: '0.875rem',
            lineHeight: 1.66,
            letterSpacing: '0.4px',
            color: TextSecondary,
          },
          overline: {
            fontFamily: fontFamilyBody,
            fontWeight: fontWeightRegular,
            fontSize: '0.875rem',
            lineHeight: 2.66,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: TextSecondary,
          },
          // `button/medium` in Figma: Medium 14 on a 24px line. llama has this
          // as Regular 16, so button text gets smaller and a little heavier.
          button: {
            fontFamily: fontFamilyBody,
            fontWeight: fontWeightMedium,
            fontSize: '0.875rem',
            lineHeight: '24px',
            letterSpacing: '0.4px',
            textTransform: 'none',
          },
          // Caption's metrics, monospace face. See the note by its declaration:
          // this is Loft tooling chrome, not a brand variant.
          mono: {
            fontFamily: 'monospace',
            fontWeight: fontWeightRegular,
            fontSize: '0.875rem',
            lineHeight: 1.66,
            letterSpacing: '0.4px',
            color: TextSecondary,
          },
          // Figma's `Display 1`: Lora SemiBold Italic 36.
          //
          // Its lineHeight variable reads `100`, and Figma mixes units in this
          // file — the headings use multipliers (1.167, 1.2) while button/medium
          // and table/header use px (24). 100px on 36px text is 2.8x, which is
          // not a display face, so this is read as 100%. Flagged: worth a look in
          // the file before anyone leans on it.
          display1: {
            fontFamily: fontFamilyHeader,
            fontStyle: 'italic',
            fontWeight: fontWeightSemiBold,
            fontSize: '2.25rem',
            lineHeight: 1,
            letterSpacing: 0,
            color: TextTitle,
          },
        },
        // Back to MUI's original 8px scale: spacing(n) === n * 8. llama's own
        // scale is the non-linear spacingTokens array (1=4, 2=12, 4=20 …), so
        // every step except 12 moves — p={4} goes 20px to 32px. Declared as a
        // function, not the number 8: llama's theme already has a spacing
        // function, and merging a bare number over it leaves the number in
        // place rather than being wrapped, so theme.spacing stops being callable.
        spacing: (factor: number) => `${factor * 8}px`,
        // The shape collection is down to none (0), a 4px default, and the 9999
        // button pill. Only the default lives on the theme; the pill is already
        // set per-component in llama, and `none` is just 0.
        shape: { borderRadius: 4 },
      });
      // createTheme deep-merges, so llama's retired lavender scale survives the
      // palette override. Drop it here too, or it keeps showing up anywhere the
      // theme is read live (the karehero-colors story enumerates palette.custom).
      delete (next.palette.custom as Record<string, unknown>).lavender;
      return next;
    }}
  >
    {children}
  </MuiThemeProvider>
);

export const WithThemeProvider = ({ children }: any) => {
  return (
    <ThemeProvider>
      <PrototypeOverrides>{children}</PrototypeOverrides>
    </ThemeProvider>
  );
};
